import React, { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import sanityClient from "../../client";

const normalizeDate = (date) => {
  if (!date) return "";
  return date.replace(/\bPresent\b/gi, "Now");
};

const isCurrentRole = (exp) =>
  exp.is_current || (exp.Date && /\b(Present|Now)\b/i.test(exp.Date));

export function Experience({ className, ...props }) {
  const [experiences, setExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "experience-data"] {
          Company_Name,
          image {
            asset-> {
              url
            }
          },
          Role,
          Location,
          Date,
          Icons_For_Tech_Used,
          Description,
          is_current
        }`
      )
      .then((data) => {
        const sortedData = [...data].sort((a, b) => {
          if (a.is_current && !b.is_current) return -1;
          if (!a.is_current && b.is_current) return 1;

          const aHasPresent = a.Date && a.Date.includes("Present");
          const bHasPresent = b.Date && b.Date.includes("Present");

          if (aHasPresent && !bHasPresent) return -1;
          if (!aHasPresent && bHasPresent) return 1;

          return 0;
        });

        setExperiences(sortedData);
        setIsLoading(false);
        if (data.length === 0) {
          setError(true);
        }
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
        setError(true);
      });
  }, []);

  return (
    <section
      id="experience"
      className={cn(
        "mx-auto w-full max-w-[40rem] px-6 pt-28 pb-16 sm:pt-36 sm:pb-24",
        className
      )}
      {...props}
    >
      <h1 className="text-3xl sm:text-[2.5rem] font-medium tracking-[-0.03em] text-darkgray">
        Experience
      </h1>

      {isLoading ? (
        <ul className="mt-12 border-t border-border" aria-hidden="true">
          {[...Array(3)].map((_, i) => (
            <li key={i} className="py-7 border-b border-border space-y-2.5">
              <div className="loading-skeleton h-4 w-1/2 rounded-sm"></div>
              <div className="loading-skeleton h-3 w-1/3 rounded-sm"></div>
              <div className="loading-skeleton h-3 w-full rounded-sm"></div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <p className="mt-10 text-[15px] text-gray">
          Experience data currently unavailable.
        </p>
      ) : (
        <ul className="mt-12 border-t border-border">
          {experiences.map((exp, index) => {
            const current = isCurrentRole(exp);
            return (
              <li
                key={index}
                className="grid grid-cols-[2rem_1fr] gap-x-4 py-7 border-b border-border"
              >
                <div className="pt-0.5">
                  {exp.image?.asset?.url ? (
                    <img
                      src={exp.image.asset.url}
                      alt=""
                      className="h-8 w-8 object-contain"
                    />
                  ) : null}
                </div>

                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-4">
                    <h2 className="text-[16px] sm:text-[17px] font-medium text-darkgray flex items-center gap-2 min-w-0">
                      {current && (
                        <span
                          className="live-dot"
                          aria-label="Current role"
                          title="Current role"
                        />
                      )}
                      <span>{exp.Role}</span>
                    </h2>
                    <span
                      className={cn(
                        "text-[13px] whitespace-nowrap",
                        current ? "text-darkgray" : "text-lightgray"
                      )}
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {normalizeDate(exp.Date)}
                    </span>
                  </div>

                  <p className="mt-1 text-[14px] text-gray">
                    {exp.Company_Name}
                    {exp.Location ? (
                      <span className="text-lightgray"> · {exp.Location}</span>
                    ) : null}
                  </p>

                  {exp.Description && (
                    <p className="mt-3 text-[15px] leading-relaxed text-gray">
                      {exp.Description}
                    </p>
                  )}

                  {exp.Icons_For_Tech_Used &&
                    exp.Icons_For_Tech_Used.length > 0 && (
                      <div className="mt-3 flex items-center gap-2.5 text-lightgray">
                        {exp.Icons_For_Tech_Used.map((tech, i) => (
                          <div
                            key={i}
                            className="text-[15px]"
                            dangerouslySetInnerHTML={{ __html: tech }}
                            title="Technology used"
                          />
                        ))}
                      </div>
                    )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
